import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getContactsByCustomerId } from '@/lib/demo-data/customer-communications-contacts';
import { useMode } from '@/providers/ModeProvider';
import { createPromiseDemoResponse, linkInvoiceDemoResponse } from '@/services/demo/create-promise-demo.service';
import type { ContactApi, ContactsApiResponse } from '@/types/api/customer-detail';
import { isStubResponse } from '@/types/api/customer-detail';
import type { CustomerApi, CustomersApiResponse } from '@/types/api/customers';
import type { Transaction } from '@/types/transaction';
import { useApi } from './useApi';
import type { CollectionCaseListItemApi } from './useCollectionCasesData';
import { useModeAwareMutation, useModeAwareQuery } from './useModeAwareQuery';
import { useCustomerTransactions } from './useTransactionsData';

const EMPTY_CONTACTS: ContactApi[] = [];

export interface ConfirmationMethodApi {
  code: string;
  name: string;
}

export interface CreatePromiseApiResponse {
  promiseId: string;
  customerId: number;
  customerName: string;
  promiseDate: string;
  promisedAmount: number;
  status: string;
  confirmationMethod: string;
  createdBy: string;
  assignedUserId: number;
  assignedUserName?: string;
  createdAt: string;
  notes?: string;
}

export interface CreatePromiseInvoiceItem {
  transactionId: number;
  invoiceNumber: string;
  invoiceDate?: string;
  originalAmount: number;
  promisedAmount: number;
}

export interface CreatePromiseApiRequest {
  customerId: number;
  caseId?: number;
  contactId?: number;
  promiseDate: string;
  promisedAmount: number;
  confirmationMethodId: number;
  assignedUserId: number;
  notes?: string;
  sendConfirmation?: boolean;
  linkedInvoices?: CreatePromiseInvoiceItem[];
}

const CONFIRMATION_METHOD_ID_MAP: Record<string, number> = {
  phone: 1,
  email: 2,
  portal: 3,
  remittance: 4,
};

const MOCK_CONFIRMATION_METHODS: ConfirmationMethodApi[] = [
  { code: 'Phone', name: 'Phone Call' },
  { code: 'Email', name: 'Email' },
  { code: 'Portal', name: 'Customer Portal' },
  { code: 'Remittance', name: 'Remittance' },
];

export function useConfirmationMethods() {
  const { apiGet } = useApi();

  return useModeAwareQuery<ConfirmationMethodApi[], Error>(
    ['promises-confirmation-methods'],
    async ({ signal }) => {
      const response = await apiGet<ConfirmationMethodApi[]>(
        '/api/v1/promises/confirmation-methods',
        undefined,
        signal,
      );
      return response;
    },
    MOCK_CONFIRMATION_METHODS,
    { staleTime: 5 * 60 * 1000 },
  );
}

export function getConfirmationMethodId(code: string): number {
  return CONFIRMATION_METHOD_ID_MAP[code.toLowerCase()] ?? 1;
}

export function useCreatePromiseMutation() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CreatePromiseApiResponse, Error, CreatePromiseApiRequest>(
    async (data) => {
      return apiPost<CreatePromiseApiResponse>('/api/v1/promises', data);
    },
    createPromiseDemoResponse,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['promises-to-pay'] });
        queryClient.invalidateQueries({
          queryKey: ['promises-to-pay-dashboard'],
        });
        // Refresh transaction grids so the hasPromise flag updates
        queryClient.invalidateQueries({ queryKey: ['transactions-graphql-list'] });
        queryClient.invalidateQueries({ queryKey: ['transactions-graphql-count'] });
        queryClient.invalidateQueries({ queryKey: ['customer-transactions-graphql-list'] });
        queryClient.invalidateQueries({ queryKey: ['customer-transactions-graphql-count'] });
      },
    },
  );
}

export interface CreatePromiseFormCustomer {
  id: string;
  name: string;
  accountNumber: string;
  email: string;
  phone: string;
  creditControllerId?: string;
}

function mapCustomerApiToFormCustomer(customer: CustomerApi): CreatePromiseFormCustomer {
  return {
    id: customer.id,
    name: customer.name,
    accountNumber: customer.accountNumber,
    email: customer.contact ?? '',
    phone: customer.phone ?? '',
    creditControllerId: customer.creditControllerId,
  };
}

export interface CreatePromiseFormTransaction {
  id: string;
  headerReference: string;
  invoiceDate: Date;
  originalAmount: number;
  balanceRemaining: number;
  daysOverdue: number;
  dueDate: Date | null;
  anticipatedDate: Date | null;
  transactionType: string | null;
  outstandingAmount: number | null;
  currency: string | null;
  description: string | null;
  accountCode: string | null;
  netAmount: number | null;
  vatAmount: number | null;
  subLedger: string | null;
  orderNumber: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
  onDispute: boolean | null;
  status: string | null;
}

export function mapTransactionToFormTransaction(transaction: Transaction): CreatePromiseFormTransaction {
  const today = new Date();
  const dueDate = transaction.dueDate ? new Date(transaction.dueDate) : new Date();
  const daysOverdue = Math.max(0, Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));

  return {
    id: String(transaction.id),
    headerReference: transaction.referenceNumber ?? `TXN-${transaction.id}`,
    invoiceDate: transaction.transactionDate ? new Date(transaction.transactionDate) : new Date(),
    originalAmount: transaction.amount ?? 0,
    balanceRemaining: transaction.displayOutstandingAmount ?? transaction.outstandingAmount ?? 0,
    daysOverdue,
    dueDate: transaction.dueDate ? new Date(transaction.dueDate) : null,
    anticipatedDate: transaction.anticipatedDate ? new Date(transaction.anticipatedDate) : null,
    transactionType: transaction.transactionType ?? null,
    outstandingAmount: transaction.outstandingAmount ?? null,
    currency: transaction.currency ?? null,
    description: transaction.description ?? null,
    accountCode: transaction.accountCode ?? null,
    netAmount: transaction.netAmount ?? null,
    vatAmount: transaction.vatAmount ?? null,
    subLedger: transaction.subLedger ?? null,
    orderNumber: transaction.orderNumber ?? null,
    createdBy: transaction.createdBy ?? null,
    modifiedBy: transaction.modifiedBy ?? null,
    onDispute: transaction.onDispute ?? null,
    status: transaction.status ?? null,
  };
}

const CUSTOMER_SEARCH_PAGE_SIZE = 20;

const MOCK_CUSTOMERS_FOR_SEARCH: CreatePromiseFormCustomer[] = [
  {
    id: '1',
    name: 'Acme Corporation',
    accountNumber: 'ACC-001',
    email: 'accounts@acme.com',
    phone: '+44 20 7946 0301',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    accountNumber: 'ACC-002',
    email: 'finance@globalind.com',
    phone: '+44 121 496 0304',
  },
  {
    id: '3',
    name: 'Tech Solutions Inc',
    accountNumber: 'ACC-003',
    email: 'billing@techsol.com',
    phone: '+44 161 850 0306',
  },
];

function filterMockCustomers(customers: CreatePromiseFormCustomer[], search: string): CreatePromiseFormCustomer[] {
  if (!search.trim()) return customers;
  const lower = search.toLowerCase();
  return customers.filter((x) => x.name.toLowerCase().includes(lower) || x.accountNumber.toLowerCase().includes(lower));
}

export interface CreatePromiseCustomerSearchResult {
  customers: CreatePromiseFormCustomer[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
}

export function useCreatePromiseCustomerSearch(search: string): CreatePromiseCustomerSearchResult {
  const { apiGet } = useApi();
  const { isDemo } = useMode();
  const [customers, setCustomers] = useState<CreatePromiseFormCustomer[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSearchRef = useRef(search);
  const lastPageRef = useRef(1);

  const fetchCustomers = useCallback(
    async (searchValue: string, pageNumber: number, append: boolean) => {
      if (isDemo) {
        const filtered = filterMockCustomers(MOCK_CUSTOMERS_FOR_SEARCH, searchValue);
        setCustomers(filtered);
        setHasMore(false);
        setIsLoading(false);
        setIsFetchingMore(false);
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await apiGet<CustomersApiResponse>(
          '/api/v1/customers',
          {
            page: pageNumber,
            pageSize: CUSTOMER_SEARCH_PAGE_SIZE,
            search: searchValue || undefined,
            sortBy: 'Name',
            sortOrder: 'asc',
          },
          controller.signal,
        );

        const mapped = response.data.map(mapCustomerApiToFormCustomer);

        if (append) {
          setCustomers((x) => [...x, ...mapped]);
        } else {
          setCustomers(mapped);
        }

        setHasMore(response.pagination.hasNextPage);
      } catch (error: unknown) {
        if ((error as Error)?.name === 'AbortError') return;
        if (!append) {
          setCustomers([]);
        }
        setHasMore(false);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [apiGet, isDemo],
  );

  useEffect(() => {
    lastSearchRef.current = search;
    lastPageRef.current = 1;
    setIsLoading(true);
    setIsFetchingMore(false);
    fetchCustomers(search, 1, false);
  }, [search, fetchCustomers]);

  const loadMore = useCallback(() => {
    if (isFetchingMore || !hasMore) return;
    const nextPage = lastPageRef.current + 1;
    lastPageRef.current = nextPage;
    setIsFetchingMore(true);
    fetchCustomers(lastSearchRef.current, nextPage, true);
  }, [isFetchingMore, hasMore, fetchCustomers]);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    lastSearchRef.current = '';
    lastPageRef.current = 1;
    setCustomers([]);
    setHasMore(false);
    setIsLoading(false);
    setIsFetchingMore(false);
  }, []);

  return { customers, isLoading, isFetchingMore, hasMore, loadMore, reset };
}

export function useCreatePromiseCustomers() {
  const { apiGet } = useApi();

  const query = useModeAwareQuery<CreatePromiseFormCustomer[], Error>(
    ['create-promise-customers'],
    async ({ signal }) => {
      const response = await apiGet<CustomersApiResponse>(
        '/api/v1/customers',
        { page: 1, pageSize: 500, sortBy: 'Name', sortOrder: 'asc' },
        signal,
      );
      return response.data.map(mapCustomerApiToFormCustomer);
    },
    MOCK_CUSTOMERS_FOR_SEARCH,
  );

  return {
    customers: query.data ?? [],
    isLoading: query.isLoading,
  };
}

export function useCreatePromiseTransactions(customerId: string | undefined) {
  const transactionsQuery = useCustomerTransactions(customerId, { pageSize: 0 });

  return {
    transactions: (transactionsQuery.transactions ?? []).map(mapTransactionToFormTransaction),
    isLoading: transactionsQuery.isLoading,
  };
}

export function useCreatePromiseContacts(customerId: string | undefined) {
  const { apiGet } = useApi();
  const demoContacts = customerId ? getContactsByCustomerId(customerId) : EMPTY_CONTACTS;

  const query = useModeAwareQuery<ContactApi[], Error>(
    ['create-promise-contacts', customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<ContactsApiResponse | { message: string }>(
        `/api/v1/customers/${customerId}/contacts`,
        undefined,
        signal,
      );
      if (isStubResponse(response)) return [];
      return (response as ContactsApiResponse).contacts;
    },
    demoContacts,
    { enabled: !!customerId },
  );

  return {
    contacts: query.data ?? EMPTY_CONTACTS,
    isLoading: query.isLoading,
  };
}

export interface CreatePromiseFormCollectionCase {
  id: string;
  caseNumber: string;
  status: string;
}

const EMPTY_CASES: CreatePromiseFormCollectionCase[] = [];

const MOCK_COLLECTION_CASES: CollectionCaseListItemApi[] = [
  {
    caseId: '1',
    caseNumber: 'CC-20240101-MOCK0001',
    customerId: 1,
    companyName: null,
    customerName: 'Acme Corporation',
    customerAccountNumber: 'ACC-001',
    customerEmail: null,
    customerPhone: null,
    priority: 'High',
    status: 'Active',
    strategyId: null,
    strategyName: null,
    amount: 5000,
    daysInCollection: 30,
    nextAction: null,
    nextActionDate: null,
    riskLevel: null,
    assignedTo: null,
    assignedToName: null,
    contactId: null,
    notes: null,
    currencyCode: 'GBP',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: null,
    activityCount: 0,
    transactionCount: 0,
    lastActionType: null,
    lastActionDescription: null,
    lastActionActorName: null,
    lastActionDate: null,
    currentStage: 0,
    totalStages: 1,
  },
  {
    caseId: '2',
    caseNumber: 'CC-20240115-MOCK0002',
    customerId: 1,
    companyName: null,
    customerName: 'Acme Corporation',
    customerAccountNumber: 'ACC-001',
    customerEmail: null,
    customerPhone: null,
    priority: 'Medium',
    status: 'Active',
    strategyId: null,
    strategyName: null,
    amount: 3500,
    daysInCollection: 15,
    nextAction: null,
    nextActionDate: null,
    riskLevel: null,
    assignedTo: null,
    assignedToName: null,
    contactId: null,
    notes: null,
    currencyCode: 'GBP',
    createdAt: '2024-01-15T00:00:00Z',
    updatedAt: null,
    activityCount: 0,
    transactionCount: 0,
    lastActionType: null,
    lastActionDescription: null,
    lastActionActorName: null,
    lastActionDate: null,
    currentStage: 0,
    totalStages: 1,
  },
  {
    caseId: '3',
    caseNumber: 'CC-20240201-MOCK0003',
    customerId: 2,
    companyName: null,
    customerName: 'Global Industries Ltd',
    customerAccountNumber: 'ACC-002',
    customerEmail: null,
    customerPhone: null,
    priority: 'Low',
    status: 'Active',
    strategyId: null,
    strategyName: null,
    amount: 2000,
    daysInCollection: 10,
    nextAction: null,
    nextActionDate: null,
    riskLevel: null,
    assignedTo: null,
    assignedToName: null,
    contactId: null,
    notes: null,
    currencyCode: 'GBP',
    createdAt: '2024-02-01T00:00:00Z',
    updatedAt: null,
    activityCount: 0,
    transactionCount: 0,
    lastActionType: null,
    lastActionDescription: null,
    lastActionActorName: null,
    lastActionDate: null,
    currentStage: 0,
    totalStages: 1,
  },
  {
    caseId: '4',
    caseNumber: 'CC-20240210-MOCK0004',
    customerId: 3,
    companyName: null,
    customerName: 'Tech Solutions Inc',
    customerAccountNumber: 'ACC-003',
    customerEmail: null,
    customerPhone: null,
    priority: 'High',
    status: 'On Hold',
    strategyId: null,
    strategyName: null,
    amount: 8000,
    daysInCollection: 45,
    nextAction: null,
    nextActionDate: null,
    riskLevel: null,
    assignedTo: null,
    assignedToName: null,
    contactId: null,
    notes: null,
    currencyCode: 'GBP',
    createdAt: '2024-02-10T00:00:00Z',
    updatedAt: null,
    activityCount: 0,
    transactionCount: 0,
    lastActionType: null,
    lastActionDescription: null,
    lastActionActorName: null,
    lastActionDate: null,
    currentStage: 0,
    totalStages: 1,
  },
];

function mapCaseToFormCase(c: CollectionCaseListItemApi): CreatePromiseFormCollectionCase {
  return {
    id: c.caseId,
    caseNumber: c.caseNumber,
    status: c.status,
  };
}

export function useCreatePromiseCollectionCases(customerId: string | undefined) {
  const { apiGet } = useApi();
  const mockCasesForCustomer = customerId
    ? MOCK_COLLECTION_CASES.filter((c) => String(c.customerId) === customerId).map(mapCaseToFormCase)
    : EMPTY_CASES;

  const query = useModeAwareQuery<CreatePromiseFormCollectionCase[], Error>(
    ['create-promise-collection-cases', customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<CollectionCaseListItemApi[]>(
        `/api/v1/collection-cases/customer/${customerId}`,
        undefined,
        signal,
      );
      return response.map(mapCaseToFormCase);
    },
    mockCasesForCustomer,
    { enabled: !!customerId },
  );

  return {
    cases: query.data ?? EMPTY_CASES,
    isLoading: query.isLoading,
  };
}

export interface LinkInvoiceRequest {
  transactionId: number;
  invoiceNumber?: string;
  promisedAmount: number;
}

export function useLinkInvoiceMutation() {
  const { apiPost } = useApi();

  return useModeAwareMutation<unknown, Error, { promiseId: number; invoice: LinkInvoiceRequest }>(
    async ({ promiseId, invoice }) => {
      return apiPost(`/api/v1/promises/${promiseId}/invoices`, invoice);
    },
    linkInvoiceDemoResponse,
  );
}
